import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  RootState,
  fetchOrderInfo,
  fetchAPIData,
  fetchAPIVrData,
  saveAPI,
  MarkerType,
  MarkerDecorationType,
  ImageType,
} from '../../store';

// Import types directly to avoid circular dependency
import { PreferencesType, DEFAULT_PREFERENCES } from '../types';

import { interactiveURL } from '../../config';

// Create models slice, with models data from API
export const mapSlice = createSlice({
  name: 'map',
  initialState: {
    id: 0,
    order: {
      id: 0,
      permalink: '',
      description: '',
    },
    markers: [] as MarkerType[],
    markerId: '',
    defaultMarkers: {} as MarkerType,
    markerDecorations: [] as MarkerDecorationType[],
    default_image: 0,
    name: '',
    featured_video: '',
    featured_image: null as ImageType|null,
    groupBy: 'categories',
    allowLabels: true,
    categories: [],
    spaces: [],
    labels: [],
    images: [],
    models: [],
    current_user: {
      id: null as number|null,
      can_edit: false,
    },
    isEmbedded: false,
    needsSave: false,
    customCss: '',
    primaryColor: '',
    secondaryColor: '',
    bgColor: '',
    loaderBgColor: '',
    loaderLogoBgColor: '',
    splashImage: '',
    coverImage: '',
    updatedAt: null as string|null, // Server version the editor state is based on
    showLabels: true,
    preferences: { ...DEFAULT_PREFERENCES } as PreferencesType,
  },
  reducers: {
    setIsEmbedded: (state, { payload }) => {
      state.isEmbedded = payload;
    },
    setMapSettings: (state, { payload }) => (
      { ...state, ...payload, needsSave: true }
    ),
    setCustomCss: (state, { payload }) => {
      state.customCss = payload;
      state.needsSave = true;
    },
    setShowLabels: (state, { payload }) => {
      state.showLabels = payload as boolean;
    },
    resizeDefaultMarkers: (state, { payload: { size } }) => {
      state.defaultMarkers.size = size;
      state.needsSave = true;
    },
    // Map default marker id
    setMarkerId: (state, { payload }) => {
      state.markerId = payload;
    },
    setPreferences: (state, { payload }: PayloadAction<PreferencesType>) => {
      state.preferences = payload;
      state.needsSave = true;
    },
    updatePreference: <K extends keyof PreferencesType>(
      state: { preferences: PreferencesType; needsSave: boolean },
      { payload }: PayloadAction<{ key: K; value: PreferencesType[K] }>
    ) => {
      state.preferences[payload.key] = payload.value;

      // Adjust zoom control position to avoid sidebar button overlap
      if (payload.key === 'sidebarPosition') {
        if (payload.value === 'right' && state.preferences.zoomControlPosition === 'topright') {
          state.preferences.zoomControlPosition = 'topleft';
        } else if (payload.value === 'left' && state.preferences.zoomControlPosition === 'topleft') {
          state.preferences.zoomControlPosition = 'topright';
        }
      }

      state.needsSave = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrderInfo.fulfilled, (state, { payload }) => {
      state.order = {
        id: payload.id,
        permalink: payload.permalink,
        description: payload.description,
      };
      state.loaderBgColor = payload.loaderBgColor;
      state.loaderLogoBgColor = payload.loaderLogoBgColor;
      state.splashImage = payload.splashImage;
    });
    builder.addCase(fetchAPIVrData.fulfilled, (state, { payload }) => {
      state.primaryColor = payload.vr.primaryColor;
      state.secondaryColor = payload.vr.secondaryColor;
      state.bgColor = payload.vr.bgColor;
      state.current_user = payload.current_user;
      state.coverImage = payload.vr.thumbnail;
    });
    builder.addCase(fetchAPIData.fulfilled, (state, { payload }) => {
      state.id = payload.id;
      state.markerId = payload.markerId;
      state.defaultMarkers = payload.defaultMarkers;
      state.markers = payload.markers;
      state.markerDecorations = payload.markerDecorations;
      state.default_image = payload.default_image;
      state.name = payload.name;
      state.featured_video = payload.featured_video || '';
      state.featured_image = payload.featured_image || null;
      state.groupBy = payload.groupBy;
      state.allowLabels = payload.allowLabels;
      state.current_user = payload.current_user;
      state.customCss = payload.customCss ?? '';
      state.primaryColor = payload.primaryColor;
      state.secondaryColor = payload.secondaryColor;
      state.bgColor = payload.bgColor;
      state.coverImage = payload.coverImage;
      state.updatedAt = payload.updatedAt ?? null;
      // Load preferences from API, falling back to defaults
      // Normalize deprecated 'modal' popupStyle to 'card'
      const loadedPreferences = (payload.preferences ?? {}) as Partial<PreferencesType>;
      if ((loadedPreferences as any).popupStyle === 'modal') {
        loadedPreferences.popupStyle = 'card';
      }
      state.preferences = {
        ...DEFAULT_PREFERENCES,
        ...loadedPreferences,
      };
    });
    builder.addCase(saveAPI.fulfilled, (state, { payload }) => {
      state.needsSave = false;
      state.markers = payload.markers;
      state.markerId = payload.markerId;
      state.updatedAt = payload.updatedAt ?? null;
    });
  },
});

export const { setIsEmbedded, setMapSettings, setCustomCss, setShowLabels, resizeDefaultMarkers, setMarkerId, setPreferences, updatePreference } = mapSlice.actions;

export const mapSelector = (state: RootState) => state.map;
export const loggedInSelector = (state: RootState) => !!state.map.current_user.id;
export const canEditSelector = (state: RootState) => !!state.map.current_user.can_edit;
export const isEmbeddedSelector = (state: RootState) => state.map.isEmbedded;
export const mapUrlSelector = (state: RootState) => `${interactiveURL}/${state.map.order.id}/map/${state.map.id}`;
export const customCssSelector = (state: RootState) => state.map.customCss;
export const allowLabelsSelector = (state: RootState) => state.map.allowLabels;
export const showLabelsSelector = (state: RootState) => state.map.showLabels;
export const groupBySelector = (state: RootState) => state.map.groupBy;
export const defaultMarkersSelector = (state: RootState) => state.map.defaultMarkers;
export const orderIdSelector = (state: RootState) => state.map.order.id;
export const orderPermalinkSelector = (state: RootState) => state.map.order.permalink;
export const orderDescriptionSelector = (state: RootState) => state.map.order.description;
export const preferencesSelector = (state: RootState) => state.map.preferences;
export const sidebarEnabledSelector = (state: RootState) => state.map.preferences.sidebarEnabled;
export const popupStyleSelector = (state: RootState) => state.map.preferences.popupStyle;

// Color selectors, use preferences if set, otherwise fall back to API values
export const primaryColorSelector = (state: RootState) => (
  state.map.preferences.primaryColor ?? state.map.primaryColor
);
export const secondaryColorSelector = (state: RootState) => (
  state.map.preferences.secondaryColor ?? state.map.secondaryColor
);
export const backgroundColorSelector = (state: RootState) => (
  state.map.preferences.bgColor ?? state.map.bgColor
);
